import { useMatomo } from '@datapunt/matomo-tracker-react';
import React, { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import Onboarding from '@components/Onboarding/Onboarding';
import Header from '@components/common/Header/Header';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import useTranslation from '@hooks/useTranslation';

import { selectAppPersistOnBoardStart } from '@store/useAppStorePersist';
import { selectUseOnboardStoreOnBoardAiId } from '@store/useOnboardStore';

import FilterPanel from './components/FilterPanel/FilterPanel';
import SensorsFacilityFilter from './components/SensorsFacilityFilter/SensorsFacilityFilter';
import SensorsLocations from './components/SensorsLocations/SensorsLocations';
import { getOnboardSteps } from './helpers/getOnboardSteps';
import { useLocationsAssetsQuery } from './services/useLocationsAssetsQuery';
import {
  selectSensorAiType,
  selectSensorAiUrgency,
  selectSensorLocationId,
  selectSensorSearch,
  selectSensorWithAi,
} from './store/useSensorStore';
import { selectLastViewedSensorId, useSensorViewedStoreActions } from './store/useSensorViewedStore';
import style from './style.module.scss';

const Sensors = () => {
  const { trackPageView } = useMatomo();
  const { t } = useTranslation();

  const [steps, setSteps] = useState([]);

  const onBoardStart = selectAppPersistOnBoardStart();
  const itemId = selectUseOnboardStoreOnBoardAiId();
  const lastViewedSensorId = selectLastViewedSensorId();

  const { setLastViewedSensorId } = useSensorViewedStoreActions();

  const sensorLocationId = selectSensorLocationId();
  const sensorWithAi = selectSensorWithAi();
  const { value: aiType } = selectSensorAiType();
  const { value: aiUrgency } = selectSensorAiUrgency();
  const searchValue = selectSensorSearch();

  const [debouncedValue] = useDebouncedValue(searchValue, 500);

  const { data: locationsAssetsData, isFetching } = useLocationsAssetsQuery({
    searchValue: debouncedValue.length >= 3 ? debouncedValue : undefined,
    locationId: sensorLocationId,
    onlyWithActionItem: +sensorWithAi,
    actionItemUrgencyUi: aiUrgency,
    actionItemType: aiType,
  });

  useEffect(() => {
    trackPageView({});
    setSteps(getOnboardSteps(t, itemId, onBoardStart) as any);

    const lastViewedElement = document.getElementById(lastViewedSensorId);
    if (lastViewedElement) {
      lastViewedElement.scrollIntoView();
      setLastViewedSensorId(null);
    }
  }, []);

  return (
    <>
      <Header title={t('Asset Overview')} showLogo={true} />
      <main className={style.sensorOverviewBlock}>
        <FilterPanel />
        <div className={style.sensorOverviewBody}>
          <SensorsFacilityFilter
            sensorLocations={locationsAssetsData?.sensorsLocations?.locationIds}
            sensorFacilities={locationsAssetsData?.sensorsLocations?.facilityIds}
          />
          <SensorsLocations locationsAssetsList={locationsAssetsData?.assetsList} isFetching={isFetching} />
        </div>
        <Tooltip id="legends" className={style.tooltipLegends} />
        <Onboarding steps={steps} />
      </main>
    </>
  );
};

export default Sensors;
